import { Grid } from "@mui/material";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { DateValidationError } from "@mui/x-date-pickers/models/validation";
import { OLButton } from "OLButton";
import { mediaQuery, OLTheme } from "contexts/Theme/ThemeProvider";
import dayjs, { Dayjs } from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { inputStyles } from "../styles";
import CustomPickersDay from "./CustomPickerDay";

dayjs.extend(isBetweenPlugin);

enum LastSelected {
  START_DATE = "startDate",
  END_DATE = "endDate",
}

type FormValues = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    modal: {
      alignItems: "center",
      backgroundColor: `${theme.primary500}80`,
      display: "flex",
      justifyContent: "center",
      [`@media (max-width: ${mediaQuery.desktop}px)`]: {
        display: "none",
      },
    },
    btnWrapper: {
      display: "flex",
      gap: theme.spacing(5),
    },
    wrapper: {
      display: "flex",
      flexDirection: "column",
    },
    col: {
      display: "flex",
      backgroundColor: theme.white500,
      padding: "24px 16px",
    },
    dataPicker: {
      marginTop: "16px",
    },
    content: {
      width: "720px",
    },
    calendarWrapper: {
      padding: "8px 0",
      backgroundColor: theme.white50,
    },
    input: inputStyles,
  }),
);

const renderCustomRangePeriod =
  (values: { startDate: Dayjs | null; endDate: Dayjs | null }) =>
  (pickersDayProps: PickersDayProps<Dayjs>) => {
    const { startDate, endDate } = values;

    // Render default PickerDay when no period selected
    if (!startDate || !endDate) {
      return <PickersDay {...pickersDayProps} />;
    }

    const dayIsBetween = pickersDayProps.day.isBetween(
      startDate,
      endDate,
      null,
      "[]",
    );
    const isFirstDay = pickersDayProps.day.isSame(startDate, "day");
    const isLastDay = pickersDayProps.day.isSame(endDate, "day");

    return (
      <CustomPickersDay
        {...pickersDayProps}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  onSubmit: (startDate: Date, endDate: Date) => void;
  startDate: string | null;
  endDate: string | null;
  "data-test-id"?: string;
}

const DateRangeWithModal = ({
  isOpen,
  closeModal,
  onSubmit,
  startDate,
  endDate,
  "data-test-id": dataTestId,
}: Props) => {
  const [error, setError] = useState<DateValidationError | null>(null);
  const [lastSelected, setLastSelected] = useState<LastSelected | null>(null);
  const { message } = useMessage();
  const classes = useStyles();

  const minDate = dayjs().subtract(10, "year").startOf("year");

  const errorMessage = useMemo(() => {
    switch (error) {
      case "minDate": {
        return message(
          { id: "pleaseSelectDateOnOrAfter" },
          { date: minDate.format("MMM D, YYYY") },
        );
      }
      default: {
        return "";
      }
    }
  }, [error, minDate, message]);

  const selectStartDate = (
    date: Dayjs | null,
    setFieldValue: (field: string, val: Dayjs | null) => void,
    values: FormValues,
  ) => {
    const { startDate, endDate } = values;

    if (!date) {
      setLastSelected(LastSelected.END_DATE);
      setFieldValue("startDate", null);
    } else {
      if (startDate) {
        const isBothDatesSet = startDate && endDate;

        if (isBothDatesSet) {
          const isStartDateAfterEndDate = date.isAfter(endDate);

          if (isStartDateAfterEndDate) {
            setLastSelected(LastSelected.START_DATE);
            setFieldValue("startDate", date);
            setFieldValue("endDate", null);
          } else {
            setLastSelected(LastSelected.START_DATE);
            setFieldValue("startDate", date);
          }
        }
      } else {
        setLastSelected(LastSelected.START_DATE);
        setFieldValue("startDate", date);
      }
    }
  };

  const selectEndDate = (
    date: Dayjs | null,
    setFieldValue: (field: string, val: Dayjs | null) => void,
    values: FormValues,
  ) => {
    const { startDate } = values;

    if (!date) {
      setLastSelected(LastSelected.START_DATE);
      setFieldValue("endDate", null);
    } else {
      const isEndDateBeforeStartDate = startDate
        ? date.isBefore(startDate)
        : false;

      if (isEndDateBeforeStartDate) {
        setLastSelected(LastSelected.START_DATE);
        setFieldValue("startDate", date);
        setFieldValue("endDate", null);
      } else {
        setLastSelected(LastSelected.END_DATE);
        setFieldValue("endDate", date);
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      className={classes.modal}
      onClose={() => closeModal()}
      data-test-id={`${dataTestId}-date-range-modal`}
    >
      <div className={classes.content}>
        <Formik
          initialValues={{
            startDate: startDate ? dayjs(startDate) : null,
            endDate: endDate ? dayjs(endDate) : null,
          }}
          onSubmit={({ startDate, endDate }: FormValues) => {
            return onSubmit(
              (startDate as Dayjs).toDate(),
              (endDate as Dayjs).toDate(),
            );
          }}
          validationSchema={Yup.object().shape({
            startDate: Yup.string()
              .required(message({ id: "rangePickerRequired" }))
              .test(
                "isValidDate",
                message({ id: "rangePickerInvalidDate" }),
                (value) => {
                  const isValidDate = dayjs(value).isValid();
                  const isFromFutureDate = dayjs(value).isAfter(dayjs());

                  if (isFromFutureDate) return false;
                  return isValidDate;
                },
              )

              .nullable(),
            endDate: Yup.string()
              .required(message({ id: "rangePickerRequired" }))
              .test(
                "isValidDate",
                message({ id: "rangePickerInvalidDate" }),
                (value) => {
                  const isValidDate = dayjs(value).isValid();
                  const isFromFutureDate = dayjs(value).isAfter(dayjs());

                  if (isFromFutureDate) return false;
                  return isValidDate;
                },
              )
              .test(
                "isValidPeriod",
                message({ id: "rangePickerInvalidRangePeriod" }),
                (value, { parent }) => {
                  const { startDate } = parent;
                  const isValidStartDate = dayjs(startDate).isValid();

                  if (!isValidStartDate) {
                    return true;
                  }
                  const isValidPeriod: boolean =
                    dayjs(value).isAfter(startDate);

                  return isValidPeriod;
                },
              )

              .nullable(),
          })}
        >
          {({ handleSubmit, errors, setFieldValue, values }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container columnSpacing={8}>
                    <Grid item xs={6}>
                      <div className={classes.calendarWrapper}>
                        <StaticDatePicker
                          disableFuture
                          displayStaticWrapperAs="desktop"
                          value={null}
                          disableHighlightToday={true}
                          onChange={(selectedDate) => {
                            if (
                              lastSelected === LastSelected.END_DATE ||
                              !lastSelected
                            ) {
                              selectStartDate(
                                selectedDate,
                                setFieldValue,
                                values,
                              );
                            } else {
                              selectEndDate(
                                selectedDate,
                                setFieldValue,
                                values,
                              );
                            }
                          }}
                          slots={{ day: renderCustomRangePeriod(values) }}
                          minDate={minDate}
                        />
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      flexDirection="column"
                      justifyContent="space-between"
                      flex="1"
                      className={classes.col}
                    >
                      <div className={classes.wrapper}>
                        <Typography fontWeight={500} mb="28px">
                          {message({
                            id: "rangePickerDateRange",
                          })}
                        </Typography>
                        <DatePicker
                          disableOpenPicker
                          value={values.startDate}
                          className={classes.dataPicker}
                          onChange={(date) => setFieldValue("startDate", date)}
                          format="DD/MM/YYYY"
                          label={message({
                            id: "rangePickerFrom",
                          })}
                          slotProps={{
                            textField: {
                              error: !!errors.startDate || !!errorMessage,
                              helperText: errors.startDate || errorMessage,
                              inputProps: {
                                "data-test-id": `${dataTestId}-start-date`,
                              },
                              className: classes.input,
                            },
                          }}
                          minDate={minDate}
                          onError={(newError) => setError(newError)}
                        />
                        <DatePicker
                          disableOpenPicker
                          value={values.endDate}
                          className={classes.dataPicker}
                          onChange={(date) => setFieldValue("endDate", date)}
                          format="DD/MM/YYYY"
                          label={message({
                            id: "rangePickerTo",
                          })}
                          slotProps={{
                            textField: {
                              error: !!errors.endDate,
                              helperText: errors.endDate,
                              inputProps: {
                                "data-test-id": `${dataTestId}-end-date`,
                              },
                              className: classes.input,
                            },
                          }}
                        />
                      </div>
                      <div className={classes.btnWrapper}>
                        <OLButton
                          variant="outlined"
                          fullWidth
                          size="medium"
                          onClick={closeModal}
                          data-test-id={`${dataTestId}-date-range-modal-cancel-button`}
                        >
                          {message({
                            id: "rangePickerCancel",
                          })}
                        </OLButton>
                        <OLButton
                          type="submit"
                          fullWidth
                          size="medium"
                          variant="contained"
                          data-test-id={`${dataTestId}-date-range-modal-apply-button`}
                          disabled={!!error}
                        >
                          {message({
                            id: "rangePickerApply",
                          })}
                        </OLButton>
                      </div>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};

export default DateRangeWithModal;
